import { CheckboxOptionType, Radio, Space } from 'antd';
import { RadioChangeEvent, RadioGroupProps } from 'antd/lib/radio';
import clx from 'classnames';
import { FormikValues, useField } from 'formik';
import React, { useCallback } from 'react';
import { useFormikFormContext } from '../FormikFormContext';

export interface RadioGroupInputProps<T extends FormikValues>
  extends Omit<RadioGroupProps, 'name'> {
  name: keyof T & string;

  /**
   * Permette lo sviluppo in verticale delle opzioni
   * -> un opzione per ogni riga
   */
  vertical?: boolean;
}

/**
 * Campo per input di tipo Radio.
 */
export function RadioGroupInput<T extends FormikValues>(
  props: RadioGroupInputProps<T>
) {
  const { name, vertical, options, children, ...otherProps } = props;
  const [field, meta, helpers] = useField<string>(name);
  const { disabled } = useFormikFormContext();
  const handleChange = useCallback(
    (e: RadioChangeEvent) => {
      helpers.setValue(e.target.value);
      setTimeout(() => {
        helpers.setTouched(true, true);
      }, 0);
    },
    [helpers.setValue, helpers.setTouched]
  );

  const radioOptions = options as CheckboxOptionType[];

  return (
    <Radio.Group
      {...otherProps}
      {...field}
      name={name}
      disabled={props.disabled || disabled}
      value={field.value}
      onChange={handleChange}
      className={clx({ 'radio-input-vertical': vertical })}
    >
      <Space direction="vertical">
        {radioOptions?.map(option => (
          <Radio {...option} key={`${option.value}-key`}>
            {option.label}
          </Radio>
        ))}
      </Space>
    </Radio.Group>
  );
}

RadioGroupInput.renderFieldValue = (
  value: string | number,
  props: RadioGroupInputProps<any>
) => {
  if (!props.options) return value;
  for (let i = 0; i < props.options.length; ++i) {
    const option = props.options[i];
    if (typeof option === 'string' && value === option) {
      return value;
    }
    if ((option as CheckboxOptionType).value === value) {
      return (option as CheckboxOptionType).label;
    }
  }
  return value;
};
